/**
 * Jede Tabellenzelle bekommt die Beschriftung ihrer Spalte mit.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Wozu
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Eine Liste im Büro ist eine Tabelle: acht Spalten nebeneinander, die
 * Überschrift steht einmal oben. Auf einem Bildschirm ist das die richtige
 * Form – man überfliegt Zeilen und vergleicht Spalten.
 *
 * Auf einem Telefon ist es keine Form mehr. Acht Spalten auf 390 Pixel
 * bedeuten entweder Text, der seitlich aus dem Bild läuft, oder Spalten von
 * vierzig Pixeln, in denen «12.09.2026» dreizeilig steht. Gemessen auf der
 * Express-Offerte: 697 Pixel Tabelle in einer 356 Pixel breiten Karte, die
 * Spalte «Rabatt» ausserhalb des Bildes.
 *
 * Die Lösung ist, jede Zeile untereinander zu stapeln – links die
 * Beschriftung, rechts der Wert. Dafür muss die Zelle wissen, in welcher
 * Spalte sie steht, denn die Kopfzeile ist dann ausgeblendet. Genau das tut
 * diese Datei: sie schreibt den Text der Spaltenüberschrift als `data-feld`
 * an jede Zelle, und die Regel in office.css setzt ihn davor.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum im Programm und nicht von Hand
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Es sind acht Listen mit zusammen über fünfzig Spalten. Jede von Hand zu
 * beschriften hiesse: einmal machen, und bei der nächsten neuen Spalte
 * vergessen. Dann steht dort auf dem Telefon ein Wert ohne Bezeichnung, und
 * niemand merkt es, weil am Bildschirm alles stimmt.
 *
 * Hier wird es aus der Kopfzeile genommen, die ohnehin danebensteht. Eine
 * neue Spalte ist damit automatisch beschriftet.
 *
 * Gesetzt wird nur, was fehlt: eine Zelle, die im Quelltext schon ein
 * `data-feld` trägt, behält es – so lässt sich eine Beschriftung gezielt
 * kürzer fassen als die Spaltenüberschrift.
 */

/** Einmal über alle Bürolisten laufen und beschriften. */
export function beschrifteTabellen(wurzel: ParentNode): void {
  for (const tabelle of Array.from(wurzel.querySelectorAll('table.o-data'))) {
    const kopf = Array.from(tabelle.querySelectorAll(':scope > thead > tr > th')).map((th) =>
      (th.textContent ?? '').trim()
    )
    if (kopf.length === 0) continue
    for (const zeile of Array.from(tabelle.querySelectorAll(':scope > tbody > tr'))) {
      /*
        Zellen mit colspan überspringen das Raster.

        Eine Zeile «Noch nichts erfasst» über die ganze Breite ist keine
        Spalte und bekommt keine Beschriftung – sie wäre sonst mit dem Wort
        der ersten Spalte überschrieben.
      */
      const zellen = Array.from(zeile.children) as HTMLElement[]
      if (zellen.some((z) => Number(z.getAttribute('colspan') ?? 1) > 1)) continue
      zellen.forEach((z, i) => {
        const text = kopf[i] ?? ''
        if (text && !z.hasAttribute('data-feld')) z.setAttribute('data-feld', text)
      })
    }
  }
}

/**
 * Und dasselbe, sobald sich etwas ändert.
 *
 * Die Listen laden nach, filtern und sortieren; eine einmalige Beschriftung
 * beim ersten Zeichnen wäre nach dem ersten Filterklick wieder weg. Der
 * Beobachter hängt an einem Knoten und meldet jede neue Zeile.
 *
 * Er wird gedrosselt: React zeichnet eine Liste in vielen kleinen Schritten,
 * und für jeden Schritt über alle Tabellen zu laufen wäre Arbeit ohne
 * Ergebnis. Ein Bild später ist früh genug – gesehen hat die Zeile bis dahin
 * niemand.
 */
export function beobachteTabellen(wurzel: HTMLElement): () => void {
  let geplant = 0
  const anstossen = (): void => {
    if (geplant) return
    geplant = window.requestAnimationFrame(() => {
      geplant = 0
      beschrifteTabellen(wurzel)
    })
  }
  anstossen()
  const beobachter = new MutationObserver(anstossen)
  beobachter.observe(wurzel, { childList: true, subtree: true })
  return () => {
    beobachter.disconnect()
    if (geplant) window.cancelAnimationFrame(geplant)
  }
}
